import { Icon } from "@iconify/react";
import type { UseMutationResult } from "@tanstack/react-query";
import type { RefObject } from "react";
import { toast } from "react-toastify";
import { Tooltip as ReactTooltip } from "react-tooltip";

interface FooterProps {
  isDarkMode: boolean;
  selectedSessionId: string | null;
  fileInputRef: RefObject<HTMLInputElement>;
  inputRef: RefObject<HTMLInputElement>;
  uploadAttachmentMutation: UseMutationResult<
    unknown,
    unknown,
    { chat_session_uid: string; file: File },
    unknown
  >;
  isLoading: boolean;
  showGenerateQuotationButton: boolean;
  onSendMessage: () => void;
  onGenerateQuotation?: () => void;
}

export function Footer({
  isDarkMode,
  selectedSessionId,
  fileInputRef,
  inputRef,
  uploadAttachmentMutation,
  isLoading,
  showGenerateQuotationButton,
  onSendMessage,
  onGenerateQuotation,
}: FooterProps) {
  return (
    <footer
      className={[
        "grid",
        showGenerateQuotationButton ? "grid-cols-[1fr_auto_auto]" : "grid-cols-[1fr_auto]",
        "gap-2",
        "md:gap-3",
        "items-center",
        "p-2",
        "md:p-4",
        "border-t",
        "min-w-0",
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white",
      ].join(" ")}
    >
      <div className="grid grid-cols-[auto_1fr] gap-2 items-center min-w-0">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.jpg,.jpeg,.png,.gif,.webp"
          disabled={!selectedSessionId || uploadAttachmentMutation.isLoading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (!selectedSessionId) {
              toast.error("Please select a chat session first.");
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
              return;
            }

            uploadAttachmentMutation.mutate({
              chat_session_uid: selectedSessionId,
              file,
            });
          }}
        />
        <button
          type="button"
          onClick={() => {
            if (!selectedSessionId) {
              toast.error("Please select a chat session first.");
              return;
            }
            fileInputRef.current?.click();
          }}
          disabled={!selectedSessionId || uploadAttachmentMutation.isLoading}
          className={[
            "w-8",
            "h-8",
            "md:w-10",
            "md:h-10",
            "rounded-lg!",
            "grid",
            "place-items-center",
            "cursor-pointer",
            "transition-opacity",
            isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200",
            (!selectedSessionId || uploadAttachmentMutation.isLoading) &&
              "opacity-50 cursor-not-allowed",
          ].join(" ")}
          aria-label="Upload file"
        >
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            {uploadAttachmentMutation.isLoading ? "⏳" : "📎"}
          </span>
        </button>
        <input
          ref={inputRef}
          type="text"
          placeholder="type your message..."
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSendMessage();
            }
          }}
          className={[
            "w-full",
            "min-w-0",
            "px-2",
            "md:px-4",
            "py-2",
            "border",
            "rounded-lg",
            "focus:outline-none",
            "focus:ring-2",
            isDarkMode
              ? [
                  "border-gray-600",
                  "bg-gray-700",
                  "text-gray-100!",
                  "placeholder-gray-400",
                  "focus:ring-blue-400",
                ].join(" ")
              : [
                  "border-gray-300",
                  "bg-white",
                  "text-gray-900",
                  "placeholder-gray-500",
                  "focus:ring-blue-500",
                ].join(" "),
          ].join(" ")}
        />
      </div>
      {showGenerateQuotationButton && (
        <>
          <button
            type="button"
            onClick={onGenerateQuotation}
            disabled={isLoading || !selectedSessionId}
            className={[
              "w-10",
              "h-10",
              "md:w-10",
              "md:h-10",
              "rounded-lg!",
              "grid",
              "place-items-center",
              isDarkMode ? "bg-green-600 hover:bg-green-700" : "bg-green-500 hover:bg-green-600",
              isLoading || !selectedSessionId ? "opacity-50 cursor-not-allowed" : "",
              "generate-quotation-btn",
            ].join(" ")}
            aria-label="Generate quotation"
          >
            <Icon icon="mdi:file-document-edit-outline" className="w-5 h-5 text-white" />
          </button>
          <ReactTooltip
            anchorSelect=".generate-quotation-btn"
            place="top"
            content="Generate Final Quotation"
            style={{
              backgroundColor: isDarkMode ? "#374151" : "#1f2937",
              color: "#ffffff",
              borderRadius: "0.5rem",
              padding: "0.5rem 0.75rem",
              fontSize: "0.875rem",
            }}
          />
        </>
      )}
      <button
        type="button"
        onClick={onSendMessage}
        disabled={isLoading}
        className={[
          "w-10",
          "h-10",
          "md:w-10",
          "md:h-10",
          "rounded-lg!",
          "grid",
          "place-items-center",
          isDarkMode ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-500 hover:bg-blue-600",
          isLoading ? "opacity-50 cursor-not-allowed" : "",
        ].join(" ")}
      >
        <span className="text-white text-xl">→</span>
      </button>
    </footer>
  );
}
